import React from "react";
import PropTypes from "prop-types";
// react plugin for creating charts
// @material-ui/core
import withStyles from "@material-ui/core/styles/withStyles";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";

import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import Button from "components/CustomButtons/Button.jsx";
import RequestTrainingProgram from "components/RequestTrainingProgram/RequestTrainingProgram.jsx";

import { dayjs } from "utils/time.jsx";
import { getMyUsers, generateUserTrainingPlan } from "services/advisorUsers.js";
import { getHealthMetricsData } from "services/userDataProvider.js";

const styles = {
  sectionTitle: {
    fontWeight: 600,
    marginTop: "20px",
    marginBottom: "12px",
    color: "#333",
    fontSize: "1.1rem"
  },
  plannerCardBody: {
    paddingTop: "32px",
    "& .MuiFormControl-root": {
      marginBottom: "18px"
    }
  },
  plannerCardBodyMerged: {
    padding: "0 !important",
    "& .MuiFormControl-root": {
      marginBottom: "18px"
    }
  },
  plannerHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  toggleLabel: {
    color: "#fff",
    marginRight: 0
  },
  mergedCard: {
    boxShadow: "none !important",
    border: "none",
    background: "transparent"
  },
  mergedFooter: {
    borderTop: "none !important",
    paddingLeft: "0 !important",
    paddingRight: "0 !important",
    background: "transparent"
  }
};

const PLAN_TYPE_OPTIONS = [
  { text: "Running 5W Cycle", value: "RUNNING_5W_CYCLE" },
  { text: "Cycling 5W Cycle", value: "CYCLING_5W_CYCLE" }
];

const YES_NO_OPTIONS = [
  { text: "Yes", value: true },
  { text: "No", value: false }
];

const WORKOUT_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

const WORKOUT_FREQUENCY_OPTIONS = [
  { text: "Every week", value: "weekly" },
  { text: "Every other week", value: "biweekly" },
  { text: "Every 3 weeks", value: "every_3_weeks" },
  { text: "Every month", value: "monthly" }
];

const mapRaceDistanceToKm = raceDistance => {
  const value = (raceDistance || "").toString().toLowerCase();
  if (value === "5k") return 5;
  if (value === "10k") return 10;
  if (value === "half_marathon") return 21;
  if (value === "marathon") return 42;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const toNumberOrNull = value => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

class TrainingsPlanner extends React.Component {
  state = {
    userId: null,
    users: [],
    targetDistance: 5,
    targetDate: null,
    trainingStartDate: null,
    planTargetMileage: 30,
    currentVdot: 50,
    lastFourWeeksMileage: 20,
    easyRunsPerWeek: 1,
    vdotPacesFor1k: "4:50,4:30,4:00,3:50,3:30",
    addCircuitTraining: "no",
    softStart: "no",
    lastSessionMileage: 20,
    trainingBreakDays: 10,
    currentMaxHr: 180,
    planType: "Running 5W Cycle",
    expectedRaceVdot: 50,
    addToUserCalendar: false,
    overrideExistingTrainingEvents: false,
    trainingName: "",
    workoutBlocks: [],
    requestPlanDetails: null,
    requestActor: null,
    showRequestDetails: true
  };

  // eslint-disable-next-line no-unused-vars
  generatePlan = _e => {
    let payload = JSON.parse(JSON.stringify(this.state));
    payload["vdotPacesFor1k"] = payload["vdotPacesFor1k"].split(",");
    payload["targetDate"] = dayjs(payload["targetDate"]).format("YYYY-MM-DD");
    payload["trainingStartDate"] = dayjs(payload["trainingStartDate"]).format(
      "YYYY-MM-DD"
    );
    payload["targetDistance"] = parseInt(payload["targetDistance"]);
    payload["planTargetMileage"] = parseInt(payload["planTargetMileage"]);
    payload["currentVdot"] = parseInt(payload["currentVdot"]);
    payload["lastFourWeeksMileage"] = parseInt(payload["lastFourWeeksMileage"]);
    payload["easyRunsPerWeek"] = parseInt(payload["easyRunsPerWeek"]);
    payload["lastSessionMileage"] = parseInt(payload["lastSessionMileage"]);
    payload["expectedRaceVdot"] = parseInt(payload["expectedRaceVdot"]);
    payload["trainingBreakDays"] = parseInt(payload["trainingBreakDays"]);
    payload["workoutBlocks"] = (payload["workoutBlocks"] || [])
      .filter(
        block =>
          block.workout &&
          block.workout.trim().length > 0 &&
          block.dayOfWeek &&
          block.frequency
      )
      .map(block => ({
        workout: block.workout.trim(),
        dayOfWeek: block.dayOfWeek,
        frequency: block.frequency
      }));
    generateUserTrainingPlan(this.props.axios, payload)
      .then(data => {
        let csvContent = data.planText;
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8,"
        });
        const objUrl = URL.createObjectURL(blob);
        let link = document.createElement("a");
        link.setAttribute("href", objUrl);
        link.setAttribute("download", "training_plan.csv");
        document.body.appendChild(link);
        link.click();
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.log("TP err", err);
      });
  };

  // eslint-disable-next-line no-unused-vars
  clearInputs = e => {
    this.setState({
      targetDistance: 5,
      targetDate: null,
      trainingStartDate: null,
      planTargetMileage: 30,
      currentVdot: 50,
      lastFourWeeksMileage: 20,
      easyRunsPerWeek: 1,
      vdotPacesFor1k: "4:50,4:30,4:00,3:50,3:30",
      addCircuitTraining: "no",
      softStart: "no",
      lastSessionMileage: 20,
      trainingBreakDays: 10,
      currentMaxHr: 180,
      planType: "Running 5W Cycle",
      expectedRaceVdot: 50,
      addToUserCalendar: false,
      overrideExistingTrainingEvents: false,
      trainingName: "",
      workoutBlocks: []
    });
  };

  componentDidMount() {
    const routeState = this.props.location && this.props.location.state;
    const notificationData =
      routeState && routeState.data ? routeState.data : routeState;
    const requestPlanDetails =
      notificationData && notificationData.plan_details
        ? notificationData.plan_details
        : null;
    const requestActor =
      routeState && routeState.actor ? routeState.actor : null;

    const prefillState = {
      requestPlanDetails,
      requestActor
    };

    if (requestPlanDetails) {
      const targetDistance = mapRaceDistanceToKm(
        requestPlanDetails.raceDistance
      );
      const weeklyMileage = toNumberOrNull(requestPlanDetails.weeklyMileage);
      const trainingDaysPerWeek = toNumberOrNull(
        requestPlanDetails.trainingDaysPerWeek
      );

      if (targetDistance != null) {
        prefillState.targetDistance = targetDistance;
      }
      if (requestPlanDetails.raceDate) {
        prefillState.targetDate = requestPlanDetails.raceDate;
      }
      if (weeklyMileage != null) {
        prefillState.lastFourWeeksMileage = weeklyMileage;
        prefillState.planTargetMileage = weeklyMileage;
      }
      if (requestPlanDetails.lastRaceTime) {
        prefillState.targetRaceTime = requestPlanDetails.lastRaceTime;
      }
      if (trainingDaysPerWeek != null) {
        prefillState.easyRunsPerWeek = trainingDaysPerWeek;
      }
    }

    if (requestActor != null) {
      const actorAsNumber = toNumberOrNull(requestActor);
      prefillState.userId =
        actorAsNumber != null ? actorAsNumber : requestActor;
    }

    this.setState(prefillState);

    getMyUsers(this.props.axios).then(data => {
      this.setState({
        users: data
      });

      if (requestActor != null) {
        const actorAsNumber = toNumberOrNull(requestActor);
        const targetUserId =
          actorAsNumber != null ? actorAsNumber : requestActor;
        this.handleUserChange(targetUserId);
      }
    });
  }

  onChange = name => e => {
    this.setState({ [name]: e.target.value });
  };

  selectionChange = name => value => {
    this.setState({
      [name]: value
    });
  };

  handleSelectChange = name => event => {
    this.setState({ [name]: event.target.value });
  };

  toggleRequestDetails = () => {
    this.setState(prevState => ({
      showRequestDetails: !prevState.showRequestDetails
    }));
  };

  addWorkoutBlock = () => {
    this.setState(prevState => ({
      workoutBlocks: [
        ...prevState.workoutBlocks,
        {
          workout: "",
          dayOfWeek: "Sunday",
          frequency: "weekly"
        }
      ]
    }));
  };

  removeWorkoutBlock = index => {
    this.setState(prevState => ({
      workoutBlocks: prevState.workoutBlocks.filter((_, i) => i !== index)
    }));
  };

  handleWorkoutBlockChange = (index, field) => event => {
    const value = event.target.value;
    this.setState(prevState => ({
      workoutBlocks: prevState.workoutBlocks.map((block, i) => {
        return i === index
          ? {
              ...block,
              [field]: value
            }
          : block;
      })
    }));
  };

  handleUserChange = value => {
    getHealthMetricsData(this.props.axios, value).then(data => {
      let trainingPaces = "";
      let currentVdot = data.vo2_max || "";
      if (data && data.other_data && data.other_data.vdotTrainingPaces) {
        let vdotTrainingPaces = data.other_data.vdotTrainingPaces;
        trainingPaces = `${vdotTrainingPaces["easy"]},${
          vdotTrainingPaces["marathon"]
        },${vdotTrainingPaces["threshold"]},${vdotTrainingPaces["interval"]},${
          vdotTrainingPaces["repetition"]
        }`;
      }
      this.setState({
        currentVdot: currentVdot,
        userId: value,
        vdotPacesFor1k: trainingPaces
      });
    });
  };

  renderUsersSelectionBox() {
    let userOptions =
      this.state.users &&
      this.state.users.map(user => {
        return { text: user.username, value: user.id };
      });
    return (
      <GridContainer spacing={3}>
        <GridItem xs={12} sm={12} md={12}>
          <TextField
            select
            fullWidth
            variant="outlined"
            label="User"
            value={this.state.userId}
            onChange={event => this.handleUserChange(event.target.value)}
          >
            {userOptions &&
              userOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.text}
                </MenuItem>
              ))}
          </TextField>
        </GridItem>
      </GridContainer>
    );
  }

  render() {
    // eslint-disable-next-line no-unused-vars
    const { classes } = this.props;
    const hideHeader = Boolean(this.props.hideHeader);
    const hideUserSelection = Boolean(this.props.hideUserSelection);
    const mergeWithContainer = Boolean(this.props.mergeWithContainer);
    const {
      requestPlanDetails,
      showRequestDetails,
      workoutBlocks
    } = this.state;
    const shouldShowRequestDetails = requestPlanDetails && showRequestDetails;
    const yesNoOptions = [
      { text: "Yes", value: "yes" },
      { text: "No", value: "no" }
    ];
    return (
      <div>
        <GridContainer>
          {shouldShowRequestDetails && (
            <GridItem xs={12} sm={12} md={6}>
              <Card>
                <CardHeader color="info">
                  <h4 className={classes.cardTitleWhite}>
                    Requested Plan Data
                  </h4>
                  <p className={classes.cardCategoryWhite}>
                    Read-only details from the user request notification
                  </p>
                </CardHeader>
                <CardBody>
                  <RequestTrainingProgram
                    axios={this.props.axios}
                    embedded
                    readOnly
                    readOnlyData={requestPlanDetails}
                    open={false}
                    onClose={() => {}}
                  />
                </CardBody>
              </Card>
            </GridItem>
          )}

          <GridItem xs={12} sm={12} md={shouldShowRequestDetails ? 6 : 12}>
            <Card
              className={mergeWithContainer ? classes.mergedCard : undefined}
            >
              {!hideHeader && (
                <CardHeader color="info">
                  <div className={classes.plannerHeaderRow}>
                    <h4 className={classes.cardTitleWhite}>
                      Trainings plan generator
                    </h4>
                    {requestPlanDetails && (
                      <FormControlLabel
                        className={classes.toggleLabel}
                        control={
                          <Switch
                            checked={showRequestDetails}
                            onChange={this.toggleRequestDetails}
                            color="default"
                          />
                        }
                        label="Request Details"
                        labelPlacement="start"
                      />
                    )}
                  </div>
                  <p className={classes.cardCategoryWhite}>
                    Create a plan for your next race!
                    <br />
                    <a
                      href="https://vdoto2.com/calculator/"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      VDOT Calculator
                    </a>
                  </p>
                </CardHeader>
              )}
              <CardBody
                className={
                  mergeWithContainer
                    ? classes.plannerCardBodyMerged
                    : classes.plannerCardBody
                }
              >
                {!hideUserSelection && this.renderUsersSelectionBox()}

                <GridItem xs={12} sm={12} md={12}>
                  <Typography className={classes.sectionTitle}>
                    Race Goal
                  </Typography>
                </GridItem>

                <GridContainer spacing={3}>
                  <GridItem xs={12} sm={12} md={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Target Race Distance(KMs)"
                      type="number"
                      value={this.state["targetDistance"]}
                      onChange={this.onChange("targetDistance")}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Target Time(HH:MM:SS)"
                      value={this.state["targetRaceTime"] || ""}
                      onChange={this.onChange("targetRaceTime")}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Trainings Start Date"
                      type="date"
                      value={this.state["trainingStartDate"] || ""}
                      onChange={this.onChange("trainingStartDate")}
                      InputLabelProps={{ shrink: true }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Target Race Date"
                      type="date"
                      value={this.state["targetDate"] || ""}
                      onChange={this.onChange("targetDate")}
                      InputLabelProps={{ shrink: true }}
                    />
                  </GridItem>
                </GridContainer>

                <GridItem xs={12} sm={12} md={12}>
                  <Typography className={classes.sectionTitle}>
                    Current Fitness Level
                  </Typography>
                </GridItem>

                <GridContainer spacing={3}>
                  <GridItem xs={12} sm={12} md={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Max mileage per week(KMs)"
                      type="number"
                      value={this.state["planTargetMileage"]}
                      onChange={this.onChange("planTargetMileage")}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Average weekly mileage for last four weeks(KMs)"
                      type="number"
                      value={this.state["lastFourWeeksMileage"]}
                      onChange={this.onChange("lastFourWeeksMileage")}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Current VDOT"
                      type="number"
                      value={this.state["currentVdot"]}
                      onChange={this.onChange("currentVdot")}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Expected race VDOT"
                      type="number"
                      value={this.state["expectedRaceVdot"]}
                      onChange={this.onChange("expectedRaceVdot")}
                    />
                  </GridItem>
                </GridContainer>

                <GridItem xs={12} sm={12} md={12}>
                  <Typography className={classes.sectionTitle}>
                    Training Preferences
                  </Typography>
                </GridItem>

                <GridContainer spacing={3}>
                  <GridItem xs={12} sm={12} md={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Easy runs per week"
                      type="number"
                      value={this.state["easyRunsPerWeek"]}
                      onChange={this.onChange("easyRunsPerWeek")}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="VDOT (EMTIR) Paces for one kilometer"
                      value={this.state["vdotPacesFor1k"]}
                      onChange={this.onChange("vdotPacesFor1k")}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      variant="outlined"
                      label="Soft Start (yes/no)?"
                      value={this.state["softStart"]}
                      onChange={this.handleSelectChange("softStart")}
                    >
                      {yesNoOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.text}
                        </MenuItem>
                      ))}
                    </TextField>
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      variant="outlined"
                      label="Add circuit training (yes/no)?"
                      value={this.state["addCircuitTraining"]}
                      onChange={this.handleSelectChange("addCircuitTraining")}
                    >
                      {yesNoOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.text}
                        </MenuItem>
                      ))}
                    </TextField>
                  </GridItem>
                </GridContainer>

                <GridItem xs={12} sm={12} md={12}>
                  <Typography className={classes.sectionTitle}>
                    Plan Settings
                  </Typography>
                </GridItem>

                <GridContainer spacing={3}>
                  <GridItem xs={12} sm={12} md={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Number of days on training break"
                      type="number"
                      value={this.state["trainingBreakDays"] || 0}
                      onChange={this.onChange("trainingBreakDays")}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Avg weekly mileage before the break(KMs)"
                      type="number"
                      value={this.state["lastSessionMileage"]}
                      onChange={this.onChange("lastSessionMileage")}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Max HR"
                      type="number"
                      value={this.state["currentMaxHr"] || 180}
                      onChange={this.onChange("currentMaxHr")}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      variant="outlined"
                      label="Plan Type"
                      value={this.state["planType"] || "RUNNING_5W_CYCLE"}
                      onChange={this.handleSelectChange("planType")}
                    >
                      {PLAN_TYPE_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.text}
                        </MenuItem>
                      ))}
                    </TextField>
                  </GridItem>
                  <GridItem xs={12} sm={12} md={12}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Training Plan Name"
                      value={this.state["trainingName"]}
                      onChange={this.onChange("trainingName")}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      variant="outlined"
                      label="Add to Calendar?"
                      value={this.state["addToUserCalendar"]}
                      onChange={this.handleSelectChange("addToUserCalendar")}
                    >
                      {YES_NO_OPTIONS.map(option => (
                        <MenuItem
                          key={String(option.value)}
                          value={option.value}
                        >
                          {option.text}
                        </MenuItem>
                      ))}
                    </TextField>
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      variant="outlined"
                      label="Override existing calendar events?"
                      value={this.state["overrideExistingTrainingEvents"]}
                      onChange={this.handleSelectChange(
                        "overrideExistingTrainingEvents"
                      )}
                    >
                      {YES_NO_OPTIONS.map(option => (
                        <MenuItem
                          key={String(option.value)}
                          value={option.value}
                        >
                          {option.text}
                        </MenuItem>
                      ))}
                    </TextField>
                  </GridItem>
                </GridContainer>

                <GridItem xs={12} sm={12} md={12}>
                  <Typography className={classes.sectionTitle}>
                    Specific Workout Blocks
                  </Typography>
                </GridItem>

                <GridItem xs={12} sm={12} md={12}>
                  <Button
                    type="button"
                    color="info"
                    onClick={this.addWorkoutBlock}
                  >
                    Add Workout Block
                  </Button>
                </GridItem>

                {workoutBlocks.length > 0 && (
                  <GridContainer spacing={3}>
                    {workoutBlocks.map((block, index) => (
                      <React.Fragment key={`workout-block-${index}`}>
                        <GridItem xs={12} sm={12} md={4}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            label="Workout"
                            placeholder="e.g. Strength training"
                            value={block.workout}
                            onChange={this.handleWorkoutBlockChange(
                              index,
                              "workout"
                            )}
                          />
                        </GridItem>
                        <GridItem xs={12} sm={12} md={3}>
                          <TextField
                            select
                            fullWidth
                            variant="outlined"
                            label="Day"
                            value={block.dayOfWeek}
                            onChange={this.handleWorkoutBlockChange(
                              index,
                              "dayOfWeek"
                            )}
                          >
                            {WORKOUT_DAYS.map(day => (
                              <MenuItem key={day} value={day}>
                                {day}
                              </MenuItem>
                            ))}
                          </TextField>
                        </GridItem>
                        <GridItem xs={12} sm={12} md={3}>
                          <TextField
                            select
                            fullWidth
                            variant="outlined"
                            label="Repeat"
                            value={block.frequency}
                            onChange={this.handleWorkoutBlockChange(
                              index,
                              "frequency"
                            )}
                          >
                            {WORKOUT_FREQUENCY_OPTIONS.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.text}
                              </MenuItem>
                            ))}
                          </TextField>
                        </GridItem>
                        <GridItem xs={12} sm={12} md={2}>
                          <Button
                            type="button"
                            color="danger"
                            onClick={() => this.removeWorkoutBlock(index)}
                          >
                            Remove
                          </Button>
                        </GridItem>
                      </React.Fragment>
                    ))}
                  </GridContainer>
                )}
              </CardBody>
              <CardFooter
                className={
                  mergeWithContainer ? classes.mergedFooter : undefined
                }
              >
                <Button type="button" color="gray" onClick={this.clearInputs}>
                  Clear
                </Button>
                <Button type="button" color="info" onClick={this.generatePlan}>
                  Generate Plan
                </Button>
              </CardFooter>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }
}

TrainingsPlanner.propTypes = {
  axios: PropTypes.any,
  classes: PropTypes.object.isRequired,
  hideHeader: PropTypes.bool,
  hideUserSelection: PropTypes.bool,
  mergeWithContainer: PropTypes.bool,
  location: PropTypes.shape({
    state: PropTypes.object
  })
};

export default withStyles(styles)(TrainingsPlanner);
